import { useMemo } from "react";
import { eur } from "@/lib/api";
import { Th, Td } from "@/components/common";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Trash2, Pencil, Copy, Archive, ArchiveRestore, PackagePlus } from "lucide-react";

// Presentational card for a single fixed Bundle. No authoritative business logic.
export default function BundleCard({ b, canEdit, canCost, onEdit, onDuplicate, onArchive, onUnarchive, onDelete, onCopyToMesse }) {
  const { t } = useI18n();
  const broken = b.components.some((c) => c.missing);
  const unavailable = (b.sellable || 0) <= 0;
  const ertrag = Math.round(((b.price_net || 0) - (b.components_cost || 0)) * 100) / 100;
  const sortedComponents = useMemo(
    () => [...b.components].sort((a, c) => (a.variant_sku || "\uffff").localeCompare(c.variant_sku || "\uffff", "de", { sensitivity: "base" })),
    [b.components]
  );
  const borderClass = () => {
    if (b.archived) return "opacity-60 border-dashed border-border";
    if (unavailable) return "border-2 border-destructive";
    if (broken) return "border-destructive/50";
    return "border-border";
  };
  return (
    <div className={`rounded-md border bg-card p-5 shadow-sm ${borderClass()}`} data-testid={`bundle-card-${b.sku}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-heading text-lg font-medium">{b.name}{b.archived && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground align-middle">{t("common.archived")}</span>}</div>
          <div className="text-xs font-mono text-muted-foreground">{b.sku}</div>
          <div className="mt-2 font-mono text-3xl font-bold text-foreground" data-testid={`bundle-gross-${b.sku}`}>{eur(b.price_gross)} <span className="text-xs font-medium text-muted-foreground align-middle">{t("common.inclTax")}</span></div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("bundle.sellable")}</div>
            <div className={`font-mono text-2xl font-bold ${unavailable ? "text-destructive" : "text-primary"}`} data-testid={`bundle-sellable-${b.sku}`}>{b.sellable}</div>
            {broken && <div className="text-[10px] text-destructive font-medium">{t("bundle.notSellable")}</div>}
          </div>
          {canEdit && (b.archived ? (
            <Button size="sm" onClick={() => onUnarchive(b)} data-testid={`unarchive-bundle-${b.sku}`}><ArchiveRestore className="h-4 w-4 mr-1.5" /> {t("common.unarchive")}</Button>
          ) : (
            <div className="flex items-center gap-0.5 rounded-lg border border-border bg-muted/40 p-0.5">
              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-background" title={t("common.edit")} onClick={() => onEdit(b)} data-testid={`edit-bundle-${b.sku}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-background" title={t("common.duplicate")} onClick={() => onDuplicate(b)} data-testid={`duplicate-bundle-${b.sku}`}><Copy className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-background" title={t("messebundle.title")} onClick={() => onCopyToMesse(b)} data-testid={`to-messe-bundle-${b.sku}`}><PackagePlus className="h-4 w-4" /></Button>
              <div className="mx-0.5 h-5 w-px bg-border" />
              {b.has_sales
                ? <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-background" title={t("common.archive")} onClick={() => onArchive(b)} data-testid={`archive-bundle-${b.sku}`}><Archive className="h-4 w-4" /></Button>
                : <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10" title={t("common.delete")} onClick={() => onDelete(b)} data-testid={`delete-bundle-${b.sku}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ))}
        </div>
      </div>
      <table className="w-full mt-4">
        <thead><tr><Th>{t("bundle.component")}</Th><Th right>{t("common.quantity")}</Th><Th right>{t("inv.available")}</Th></tr></thead>
        <tbody>
          {sortedComponents.map((c, i) => (
            <tr key={c.component_variant_id || i} className={c.missing ? "bg-destructive/5" : ""}>
              <Td>{c.missing
                ? <span className="text-destructive text-xs">⚠ {t("bundle.componentRemoved")}</span>
                : <span><span className="font-mono">{c.variant_sku}</span>{c.variant_name ? ` · ${c.variant_name}` : ""}</span>}
                {c.is_free_component && <span className="ml-1 text-xs text-chart-3">({t("bundle.free")})</span>}</Td>
              <Td right mono>{c.quantity}</Td><Td right mono className={(c.available || 0) <= 0 ? "text-destructive font-semibold" : ""}>{c.available}</Td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex items-center gap-6 text-sm border-t border-border pt-3">
        <span className="text-muted-foreground">{t("common.priceNet")}: <span className="font-mono text-foreground">{eur(b.price_net)}</span></span>
        {canCost && <span className="text-muted-foreground">{t("bundle.componentCost")}: <span className="font-mono text-foreground">{eur(b.components_cost)}</span></span>}
        {canCost && <span className="ml-auto text-muted-foreground">{t("bundle.yield")}: <span className={`font-mono ${ertrag < 0 ? "text-destructive font-semibold" : "text-foreground"}`} data-testid={`bundle-ertrag-${b.sku}`}>{eur(ertrag)}</span></span>}
      </div>
    </div>
  );
}
